import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../utils/api';
import toast from 'react-hot-toast';
import ContactActions from '../components/ContactActions';

export default function BookingConfirmation() {
  const [treks, setTreks] = useState([]);
  const [points, setPoints] = useState([]);
  const [generatedText, setGeneratedText] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    customerName: '', groupSize: 1, contactNumber: '', customerEmail: '',
    trekName: '', trekCode: '', duration: '', customTrek: false,
    startDate: null, endDate: null,
    pickupLocation: '', pickupTime: '', customPickup: false,
    dropoffLocation: '', dropoffTime: '', customDropoff: false,
    perPersonCost: '', advancePerPerson: '',
    miscCharges: '', divideMiscEqually: true,
    advanceDueDate: null, paymentMode: 'UPI',
    groupMembers: [],
    doubleSharingOpted: false, doubleSharingDetails: '',
    addons: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [treksRes, pointsRes] = await Promise.all([
        api.get('/bookings/data/treks'),
        api.get('/bookings/data/pickup-dropoff')
      ]);
      setTreks(treksRes.data);
      setPoints(pointsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    }
  };

  const trekOptions = [
    ...treks.map(t => ({ value: t.name, label: `${t.name}${t.defaultDuration ? ' (' + t.defaultDuration + ')' : ''}`, duration: t.defaultDuration, trekCode: t.trekCode, id: t.id, custom: t.custom })),
    { value: '__custom__', label: '+ Custom Trek Name' }
  ];

  const pickupOptions = points.filter(p => p.type === 'pickup').map(p => ({ value: p.location, label: `${p.location} (${p.time})`, time: p.time, id: p.id, custom: p.custom }));
  const dropoffOptions = points.filter(p => p.type === 'dropoff').map(p => ({ value: p.location, label: `${p.location} (${p.time})`, time: p.time, id: p.id, custom: p.custom }));

  const handleDeleteTrek = async (e, trek) => {
    e.stopPropagation();
    if (!window.confirm(`Delete trek "${trek.value}"?`)) return;
    try {
      await api.delete(`/bookings/data/treks/${trek.id}`);
      toast.success('Trek deleted');
      loadData();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete'); }
  };

  const handleDeletePoint = async (e, point) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${point.value}"?`)) return;
    try {
      await api.delete(`/bookings/data/pickup-dropoff/${point.id}`);
      toast.success('Location deleted');
      loadData();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete'); }
  };

  const handleEditTrek = (e, trek) => {
    e.stopPropagation();
    const newName = prompt('Edit trek name:', trek.value);
    if (!newName || newName === trek.value) return;
    const newDuration = prompt('Edit duration:', trek.duration || '');
    api.put(`/bookings/data/treks/${trek.id}`, { name: newName, defaultDuration: newDuration || '' })
      .then(() => { toast.success('Trek updated'); loadData(); })
      .catch(() => toast.error('Failed to update trek'));
  };

  const handleEditPoint = (e, point) => {
    e.stopPropagation();
    const newLocation = prompt('Edit location:', point.value);
    if (!newLocation || newLocation === point.value) return;
    const newTime = prompt('Edit time:', point.time || '');
    api.put(`/bookings/data/pickup-dropoff/${point.id}`, { location: newLocation, time: newTime || '' })
      .then(() => { toast.success('Location updated'); loadData(); })
      .catch(() => toast.error('Failed to update'));
  };

  const TrekOption = ({ innerProps, data, isFocused }) => (
    <div {...innerProps} className={`flex items-center justify-between px-3 py-2 cursor-pointer ${isFocused ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
      <span className="text-sm">{data.label}</span>
      {data.value !== '__custom__' && (
        <span className="flex gap-1 ml-2" onClick={e => e.stopPropagation()}>
          <button onClick={(e) => handleEditTrek(e, data)} className="text-blue-500 hover:text-blue-700 text-xs px-1">✏️</button>
          <button onClick={(e) => handleDeleteTrek(e, data)} className="text-red-500 hover:text-red-700 text-xs px-1">🗑️</button>
        </span>
      )}
    </div>
  );

  const PointOption = (deleteHandler, editHandler) => ({ innerProps, data, isFocused }) => (
    <div {...innerProps} className={`flex items-center justify-between px-3 py-2 cursor-pointer ${isFocused ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
      <span className="text-sm">{data.label}</span>
      {data.value !== '__custom__' && (
        <span className="flex gap-1 ml-2" onClick={e => e.stopPropagation()}>
          <button onClick={(e) => editHandler(e, data)} className="text-blue-500 hover:text-blue-700 text-xs px-1">✏️</button>
          <button onClick={(e) => deleteHandler(e, data)} className="text-red-500 hover:text-red-700 text-xs px-1">🗑️</button>
        </span>
      )}
    </div>
  );

  const handleTrekChange = (selected) => {
    if (selected.value === '__custom__') {
      setForm(f => ({ ...f, trekName: '', trekCode: '', duration: '', customTrek: true }));
    } else {
      setForm(f => ({ ...f, trekName: selected.value, trekCode: selected.trekCode, duration: selected.duration, customTrek: false }));
    }
  };

  const groupSize = parseInt(form.groupSize) || 1;
  const perPerson = parseFloat(form.perPersonCost) || 0;
  const advPP = parseFloat(form.advancePerPerson) || 0;
  const miscCharges = parseFloat(form.miscCharges) || 0;
  const totalCost = perPerson * groupSize + miscCharges;
  const totalAdvance = advPP * groupSize;
  const miscPerPerson = form.divideMiscEqually && groupSize > 0 ? Math.ceil(miscCharges / groupSize) : miscCharges;
  const pendingPP = perPerson - advPP + (form.divideMiscEqually ? miscPerPerson : 0);
  const totalPending = totalCost - totalAdvance;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerName || !form.trekName || !form.startDate || !form.endDate) {
      return toast.error('Please fill all required fields');
    }
    if (form.customTrek && !form.trekCode) {
      return toast.error('Please enter a trek code for the custom trek');
    }
    if (form.endDate <= form.startDate) {
      return toast.error('End date must be after start date');
    }

    setSubmitting(true);
    try {
      const payload = {
        customerName: form.customerName,
        groupSize,
        contactNumber: form.contactNumber,
        customerEmail: form.customerEmail,
        groupMembers: groupSize > 1 ? [form.customerName, ...form.groupMembers.filter(n => n.trim())] : [form.customerName],
        trekName: form.trekName,
        trekCode: form.trekCode,
        duration: form.duration,
        startDate: `${form.startDate.getFullYear()}-${String(form.startDate.getMonth()+1).padStart(2,'0')}-${String(form.startDate.getDate()).padStart(2,'0')}`,
        endDate: `${form.endDate.getFullYear()}-${String(form.endDate.getMonth()+1).padStart(2,'0')}-${String(form.endDate.getDate()).padStart(2,'0')}`,
        pickupPoint: { name: form.pickupLocation, time: form.pickupTime },
        dropoffPoint: { name: form.dropoffLocation, time: form.dropoffTime },
        pricing: {
          perPersonCost: perPerson,
          totalCost,
          advancePerPerson: advPP,
          totalAdvance,
          miscCharges,
          miscPerPerson: form.divideMiscEqually ? miscPerPerson : 0,
          pendingPerPerson: pendingPP,
          totalPending,
          advanceDueDate: form.advanceDueDate ? `${form.advanceDueDate.getFullYear()}-${String(form.advanceDueDate.getMonth()+1).padStart(2,'0')}-${String(form.advanceDueDate.getDate()).padStart(2,'0')}` : ''
        },
        paymentMode: form.paymentMode,
        doubleSharing: { opted: form.doubleSharingOpted, details: form.doubleSharingOpted ? form.doubleSharingDetails : '' },
        addons: form.addons.filter(a => a.name.trim())
      };

      const { data: booking } = await api.post('/bookings', payload);
      setBookingId(booking.id);
      toast.success('Booking created successfully!');

      // Auto-generate all documents
      const { data: doc } = await api.post(`/documents/booking-confirmation/${booking.id}`);
      setGeneratedText(doc.text);
      toast.success('Booking confirmation generated!');

      // Generate payment received email and payment reminder in background
      try {
        await api.post(`/documents/payment-email/${booking.id}?draft=true`);
      } catch (e) { /* non-critical */ }
      try {
        await api.post(`/documents/payment-reminder/${booking.id}`);
      } catch (e) { /* non-critical */ }

      // Save custom trek if new
      if (form.customTrek && form.trekName) {
        await api.post('/bookings/data/treks', { name: form.trekName, defaultDuration: form.duration, trekCode: form.trekCode });
      }

      // Save custom pickup/dropoff to the dropdown list
      if (form.customPickup && form.pickupLocation) {
        await api.post('/bookings/data/pickup-dropoff', { type: 'pickup', location: form.pickupLocation, time: form.pickupTime });
      }
      if (form.customDropoff && form.dropoffLocation) {
        await api.post('/bookings/data/pickup-dropoff', { type: 'dropoff', location: form.dropoffLocation, time: form.dropoffTime });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedText);
    toast.success('Copied to clipboard!');
  };

  if (generatedText) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-green-800">✅ Booking Created Successfully</h2>
          <p className="text-green-700 mt-1">Booking ID: <strong>{bookingId}</strong></p>
          <div className="mt-3">
            <ContactActions customerName={form.customerName} contactNumber={form.contactNumber} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Booking Confirmation Text</h3>
            <div className="space-x-2">
              <button onClick={copyToClipboard} className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700">
                📋 Copy to Clipboard
              </button>
              <button onClick={() => { setGeneratedText(''); setBookingId(''); setForm({ customerName: '', groupSize: 1, contactNumber: '', customerEmail: '', groupMembers: [], trekName: '', trekCode: '', duration: '', customTrek: false, startDate: null, endDate: null, pickupLocation: '', pickupTime: '', customPickup: false, dropoffLocation: '', dropoffTime: '', customDropoff: false, perPersonCost: '', advancePerPerson: '', miscCharges: '', divideMiscEqually: true, advanceDueDate: null, paymentMode: 'UPI', doubleSharingOpted: false, doubleSharingDetails: '', addons: [] }); }}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-200">
                ➕ New Booking
              </button>
            </div>
          </div>
          <textarea value={generatedText} onChange={e => setGeneratedText(e.target.value)}
            className="w-full h-[600px] p-4 border rounded-lg font-mono text-sm bg-gray-50 resize-none" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">🏕️ New Booking Confirmation</h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Customer Info */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">👤 Customer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
              <input type="text" value={form.customerName} onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="Pawan Kumar" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group Size *</label>
              <input type="number" min="1" value={form.groupSize} onChange={e => {
                const size = parseInt(e.target.value) || 1;
                const members = Array.from({ length: Math.max(0, size - 1) }, (_, i) => form.groupMembers[i] || '');
                setForm(f => ({ ...f, groupSize: e.target.value, groupMembers: members }));
              }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
              <input type="tel" value={form.contactNumber} onChange={e => setForm(f => ({ ...f, contactNumber: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="+91-XXXXXXXXXX" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Email</label>
              <input type="email" value={form.customerEmail} onChange={e => setForm(f => ({ ...f, customerEmail: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="customer@example.com" />
            </div>
          </div>
          {groupSize > 1 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Group Member Names</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                  1. {form.customerName || 'Primary contact (above)'}
                </div>
                {form.groupMembers.map((name, i) => (
                  <input key={i} type="text" value={name}
                    onChange={e => {
                      const members = [...form.groupMembers];
                      members[i] = e.target.value;
                      setForm(f => ({ ...f, groupMembers: members }));
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
                    placeholder={`Member ${i + 2} name`} />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Trek Selection */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">🏔️ Trek Selection</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trek Name *</label>
              {form.customTrek ? (
                <div className="flex gap-2">
                  <input type="text" value={form.trekName} onChange={e => setForm(f => ({ ...f, trekName: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="Enter trek name" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, customTrek: false, trekCode: '' }))} className="text-sm text-primary-600 hover:underline">Cancel</button>
                </div>
              ) : (
                <Select options={trekOptions} onChange={handleTrekChange} placeholder="Select trek..." 
                  className="text-sm" components={{ Option: TrekOption }} />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <input type="text" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="e.g. 12D/11N" />
            </div>
            {form.customTrek && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trek Code * <span className="text-xs text-gray-400">(e.g. 09, 10)</span></label>
                <input type="text" value={form.trekCode} onChange={e => setForm(f => ({ ...f, trekCode: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="Enter 2-digit trek code" maxLength={2} />
                <p className="text-xs text-gray-400 mt-1">This code will be used in receipt numbers (GH-XX-XXXX)</p>
              </div>
            )}
          </div>
        </section>

        {/* Dates */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">📅 Date Selection</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <DatePicker selected={form.startDate} onChange={date => setForm(f => ({ ...f, startDate: date }))}
                dateFormat="dd/MM/yyyy" placeholderText="Select start date" className="w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <DatePicker selected={form.endDate} onChange={date => setForm(f => ({ ...f, endDate: date }))}
                dateFormat="dd/MM/yyyy" placeholderText="Select end date" minDate={form.startDate} className="w-full" />
            </div>
          </div>
        </section>

        {/* Pickup & Dropoff */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">📍 Pickup & Drop Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location</label>
              {form.customPickup ? (
                <div className="flex gap-2">
                  <input type="text" value={form.pickupLocation} onChange={e => setForm(f => ({ ...f, pickupLocation: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm" placeholder="Enter custom pickup location" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, customPickup: false, pickupLocation: '', pickupTime: '' }))} className="text-sm text-primary-600 hover:underline whitespace-nowrap">Cancel</button>
                </div>
              ) : (
                <Select options={[...pickupOptions, { value: '__custom__', label: '+ Add Custom Location' }]}
                  onChange={sel => {
                    if (sel.value === '__custom__') {
                      setForm(f => ({ ...f, customPickup: true, pickupLocation: '', pickupTime: '' }));
                    } else {
                      setForm(f => ({ ...f, pickupLocation: sel.value, pickupTime: sel.time }));
                    }
                  }} placeholder="Select pickup..." className="text-sm"
                  components={{ Option: PointOption(handleDeletePoint, handleEditPoint) }} />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Time</label>
              <input type="text" value={form.pickupTime} onChange={e => setForm(f => ({ ...f, pickupTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="6:00 AM – 7:00 AM" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dropoff Location</label>
              {form.customDropoff ? (
                <div className="flex gap-2">
                  <input type="text" value={form.dropoffLocation} onChange={e => setForm(f => ({ ...f, dropoffLocation: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm" placeholder="Enter custom dropoff location" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, customDropoff: false, dropoffLocation: '', dropoffTime: '' }))} className="text-sm text-primary-600 hover:underline whitespace-nowrap">Cancel</button>
                </div>
              ) : (
                <Select options={[...dropoffOptions, { value: '__custom__', label: '+ Add Custom Location' }]}
                  onChange={sel => {
                    if (sel.value === '__custom__') {
                      setForm(f => ({ ...f, customDropoff: true, dropoffLocation: '', dropoffTime: '' }));
                    } else {
                      setForm(f => ({ ...f, dropoffLocation: sel.value, dropoffTime: sel.time }));
                    }
                  }} placeholder="Select dropoff..." className="text-sm"
                  components={{ Option: PointOption(handleDeletePoint, handleEditPoint) }} />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dropoff Time</label>
              <input type="text" value={form.dropoffTime} onChange={e => setForm(f => ({ ...f, dropoffTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="8:00 PM" />
            </div>
          </div>
        </section>

        {/* Double Sharing & Add-ons */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">🏨 Double Sharing & Add-ons</h2>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.doubleSharingOpted}
              onChange={e => setForm(f => ({ ...f, doubleSharingOpted: e.target.checked, doubleSharingDetails: e.target.checked ? f.doubleSharingDetails : '' }))}
              className="rounded border-gray-300" />
            <span className="text-sm font-medium text-gray-700">Customer opted for Double Sharing stay</span>
          </label>
          {form.doubleSharingOpted && (
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Double Sharing Details</label>
              <input type="text" value={form.doubleSharingDetails} onChange={e => setForm(f => ({ ...f, doubleSharingDetails: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="e.g. Extra ₹1,500 per person for double sharing room" />
            </div>
          )}

          <div className="mt-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Add-ons <span className="text-xs text-gray-400">(e.g. Delhi Pickup, Delhi Drop)</span></label>
            <div className="space-y-2">
              {form.addons.map((addon, i) => (
                <div key={i} className="flex gap-2">
                  <input type="text" value={addon.name}
                    onChange={e => { const addons = [...form.addons]; addons[i] = { ...addons[i], name: e.target.value }; setForm(f => ({ ...f, addons })); }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm" placeholder="Add-on name (e.g. Delhi Pickup)" />
                  <input type="text" value={addon.details}
                    onChange={e => { const addons = [...form.addons]; addons[i] = { ...addons[i], details: e.target.value }; setForm(f => ({ ...f, addons })); }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm" placeholder="Details / cost (optional)" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, addons: f.addons.filter((_, idx) => idx !== i) }))}
                    className="text-red-500 hover:text-red-700 px-2">🗑️</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setForm(f => ({ ...f, addons: [...f.addons, { name: '', details: '' }] }))}
              className="mt-2 text-sm text-primary-600 hover:underline">+ Add Add-on</button>
          </div>
        </section>

        {/* Pricing */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">💰 Pricing Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Per Person Cost (₹) *</label>
              <input type="number" min="0" value={form.perPersonCost} onChange={e => setForm(f => ({ ...f, perPersonCost: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="38999" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Package Cost</label>
              <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium">
                ₹{totalCost.toLocaleString('en-IN')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Advance Per Person (₹)</label>
              <input type="number" min="0" value={form.advancePerPerson} onChange={e => setForm(f => ({ ...f, advancePerPerson: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="7499" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Advance</label>
              <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm font-medium text-green-700">
                ₹{totalAdvance.toLocaleString('en-IN')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Miscellaneous Charges (₹)</label>
              <input type="number" min="0" value={form.miscCharges} onChange={e => setForm(f => ({ ...f, miscCharges: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="0" />
              {miscCharges > 0 && (
                <label className="flex items-center gap-2 mt-1 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={form.divideMiscEqually} onChange={e => setForm(f => ({ ...f, divideMiscEqually: e.target.checked }))}
                    className="rounded border-gray-300" />
                  Divide equally per person
                </label>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Balance Per Person</label>
              <div className="px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg text-sm font-medium text-orange-700">
                ₹{pendingPP.toLocaleString('en-IN')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Balance</label>
              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm font-medium text-red-700">
                ₹{totalPending.toLocaleString('en-IN')}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pending Balance Due Date</label>
              <DatePicker selected={form.advanceDueDate} onChange={date => setForm(f => ({ ...f, advanceDueDate: date }))}
                dateFormat="dd/MM/yyyy" placeholderText="Select due date" className="w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
              <select value={form.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none">
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Both">Both</option>
              </select>
            </div>
          </div>
        </section>

        <button type="submit" disabled={submitting}
          className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold text-lg hover:bg-primary-700 transition disabled:opacity-50 shadow-lg">
          {submitting ? '⏳ Creating Booking...' : '✅ Create Booking & Generate Confirmation'}
        </button>
      </form>
    </div>
  );
}
